/**
 * QuantumFlowSync.AI - Enhanced Employee Detail Page
 * Employee profile with tabs: Profile, Roles & Permissions, Activity Log, Sessions
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, Building, Calendar, Shield,
  Activity, Monitor, Edit, Trash2, Plus, X, Check,
  Clock, MapPin, ChevronDown, ChevronRight, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { PermissionGate, TierGate } from '../../components/PermissionGate';

const EnhancedEmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  
  // State
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Tab-specific state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [activities, setActivities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showAddRole, setShowAddRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  
  // Permissions
  const canUpdate = usePermission('employees', '*', 'update');
  const canDelete = usePermission('employees', '*', 'delete');
  const canManageRoles = usePermission('roles', '*', 'update');
  
  // Fetch employee
  const fetchEmployee = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiRequest(`/api/admin/employees/${id}`);
      
      if (response.success) {
        setEmployee(response.data);
        setRoles(response.data.roles || []);
        setPermissions(response.data.permissions || {});
      } else {
        setError(response.error || 'Failed to fetch employee');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch employee');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch activities
  const fetchActivities = async () => {
    try {
      const response = await apiRequest(`/api/admin/audit-logs/employee/${id}?limit=20`);
      
      if (response.success) {
        setActivities(response.data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    }
  };
  
  // Fetch sessions
  const fetchSessions = async () => {
    try {
      const response = await apiRequest(`/api/admin/employees/${id}/sessions`);
      
      if (response.success) {
        setSessions(response.data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };
  
  // Fetch available roles
  const fetchAvailableRoles = async () => {
    try {
      const response = await apiRequest('/api/admin/roles');
      
      if (response.success) {
        setAvailableRoles(response.data.roles || []);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };
  
  // Add role
  const addRole = async () => {
    if (!selectedRole) return;
    
    try {
      const response = await apiRequest(`/api/admin/employees/${id}/roles`, {
        method: 'POST',
        body: JSON.stringify({ role_id: selectedRole })
      });
      
      if (response.success) {
        fetchEmployee();
        setShowAddRole(false);
        setSelectedRole('');
      }
    } catch (err) {
      console.error('Failed to add role:', err);
    }
  };
  
  // Remove role
  const removeRole = async (roleId) => {
    try {
      const response = await apiRequest(`/api/admin/employees/${id}/roles/${roleId}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        fetchEmployee();
      }
    } catch (err) {
      console.error('Failed to remove role:', err);
    }
  };
  
  // Terminate session
  const terminateSession = async (sessionId) => {
    try {
      const response = await apiRequest(`/api/admin/sessions/${sessionId}/terminate`, {
        method: 'POST'
      });
      
      if (response.success) {
        fetchSessions();
      }
    } catch (err) {
      console.error('Failed to terminate session:', err);
    }
  };
  
  // Effect: Fetch employee on mount
  useEffect(() => {
    fetchEmployee();
    fetchAvailableRoles();
  }, [id]);
  
  // Effect: Fetch tab-specific data
  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivities();
    } else if (activeTab === 'sessions') {
      fetchSessions();
    }
  }, [activeTab]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (error || !employee) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200">{error || 'Employee not found'}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/employees')}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Employees
      </button>
      
      {/* Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-white" />
            </div>
            <div className="ml-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {employee.name}
              </h1>
              <div className="flex items-center mt-2 space-x-4">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <Mail className="w-4 h-4 mr-2" />
                  {employee.email}
                </div>
                {employee.department && (
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Building className="w-4 h-4 mr-2" />
                    {employee.department}
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <Calendar className="w-4 h-4 mr-2" />
                  Joined {new Date(employee.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center mt-3 space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  employee.is_active
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {employee.is_active ? 'Active' : 'Inactive'}
                </span>
                {roles.map((role) => (
                  <span
                    key={role.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    {role.display_name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <PermissionGate resource="employees" scope="*" action="update">
              <button
                onClick={() => navigate(`/admin/employees/${id}/edit`)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </PermissionGate>
            
            <PermissionGate resource="employees" scope="*" action="delete">
              <button
                onClick={() => {/* Delete confirmation */}}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'profile', label: 'Profile', icon: User },
              { id: 'roles', label: 'Roles & Permissions', icon: Shield },
              { id: 'activity', label: 'Activity Log', icon: Activity },
              { id: 'sessions', label: 'Sessions', icon: Monitor }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <p className="text-gray-900 dark:text-white">{employee.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <p className="text-gray-900 dark:text-white">{employee.email}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department
                  </label>
                  <p className="text-gray-900 dark:text-white">{employee.department || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <p className="text-gray-900 dark:text-white">{employee.phone || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    employee.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Joined Date
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(employee.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Roles & Permissions Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-6">
              {/* Roles Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Assigned Roles
                  </h3>
                  <TierGate tier={1}>
                    <button
                      onClick={() => setShowAddRole(!showAddRole)}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Role
                    </button>
                  </TierGate>
                </div>
                
                {showAddRole && (
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Select a role...</option>
                        {availableRoles
                          .filter(r => !roles.some(er => er.id === r.id))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.display_name} (Tier {role.tier})
                            </option>
                          ))}
                      </select>
                      <button
                        onClick={addRole}
                        disabled={!selectedRole}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setShowAddRole(false)}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  {roles.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">No roles assigned</p>
                  ) : (
                    roles.map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {role.display_name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Tier {role.tier}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {role.description}
                          </p>
                        </div>
                        <TierGate tier={1}>
                          <button
                            onClick={() => removeRole(role.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </TierGate>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Permissions Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Effective Permissions
                </h3>
                <div className="space-y-2">
                  {Object.entries(permissions).length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">No permissions</p>
                  ) : (
                    Object.entries(permissions).map(([resource, perms]) => (
                      <details
                        key={resource}
                        className="bg-gray-50 dark:bg-gray-900 rounded-lg"
                      >
                        <summary className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg flex items-center justify-between">
                          <span className="font-medium text-gray-900 dark:text-white capitalize">
                            {resource}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        </summary>
                        <div className="px-4 pb-3 space-y-1">
                          {Array.isArray(perms) ? perms.map((perm, idx) => (
                            <div key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                              <Check className="w-3 h-3 mr-2 text-green-600" />
                              {perm.full || `${perm.scope}:${perm.action}`}
                            </div>
                          )) : (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {JSON.stringify(perms)}
                            </div>
                          )}
                        </div>
                      </details>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Activity Log Tab */}
          {activeTab === 'activity' && (
            <div className="space-y-4">
              {activities.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No activity found
                </p>
              ) : (
                activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      activity.result === 'success'
                        ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
                        : activity.result === 'denied'
                        ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400'
                    }`}>
                      <Activity className="w-4 h-4" />
                    </div>
                    
                    <div className="ml-3 flex-1">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {activity.action.replace(/_/g, ' ')} on {activity.resource}
                      </p>
                      <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(activity.timestamp).toLocaleString()}
                        {activity.ip_address && (
                          <>
                            <MapPin className="w-3 h-3 ml-3 mr-1" />
                            {activity.ip_address}
                          </>
                        )}
                      </div>
                    </div>
                    
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      activity.result === 'success'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : activity.result === 'denied'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {activity.result}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-4">
              {sessions.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No active sessions
                </p>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <div className="flex items-center">
                      <Monitor className="w-8 h-8 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {session.device || 'Unknown Device'}
                        </p>
                        <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <MapPin className="w-3 h-3 mr-1" />
                          {session.ip_address}
                          <Clock className="w-3 h-3 ml-3 mr-1" />
                          {new Date(session.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => terminateSession(session.id)}
                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Terminate
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedEmployeeDetailPage;
